import { Buffer } from "buffer";
import { ethers } from "ethers";
import { EVM_CONFIG } from "../../config";
import { EVM_NETWORK_DEFINITIONS } from "../../config/evmNetworks";
import { deriveSpendingKeys } from "../../joinsplit/keys";
import {
  assertDogeDerivationFingerprint,
  DogeDerivationError,
  verifyDogeDerivationSignature,
} from "./dogeDerivationSafety";
import { isUserRejection } from "./walletChainSupport";
import type { EvmDerivationDescriptor } from "../../config/evmNetworks";
import {
  carryOverCommit,
  hasSavedWalletData,
  resolveCarryOver,
} from "../../joinsplit-v2/walletCarryOver";
// The descriptors of the network this page serves. Base and DogeOS Chikyu
// sign "StealthOS Private Wallet" with their own primary type and key
// version, on their own chain, pool and router, so the two never derive the
// same key. The legacy descriptors reproduce what each network signed before,
// byte for byte, for wallets that keep their keys (walletCarryOver.ts).
const DEFINITION = EVM_CONFIG.NETWORK ?? EVM_NETWORK_DEFINITIONS[8453];
const DERIVATION = DEFINITION.derivation;
const LEGACY_DERIVATION = DEFINITION.legacyDerivation;
export const BASE_DERIVATION_TYPED_DATA_VERSION = DERIVATION.version;

const LOCATION_FIELDS = [
  { name: "wallet", type: "address" },
  { name: "origin", type: "string" },
  { name: "pool", type: "address" },
  { name: "verifier", type: "address" },
];

function derivationTypes(
  descriptor: EvmDerivationDescriptor,
): Record<string, Array<{ name: string; type: string }>> {
  return {
    [descriptor.primaryType]: descriptor.statement
      ? [
          { name: "action", type: "string" },
          ...LOCATION_FIELDS,
          { name: "keyVersion", type: "string" },
        ]
      : [
          ...LOCATION_FIELDS,
          { name: "purpose", type: "string" },
          { name: "keyVersion", type: "string" },
        ],
  };
}

export interface BaseDerivationTypedData {
  version: string;
  domain: Record<string, any>;
  types: Record<string, Array<{ name: string; type: string }>>;
  message: Record<string, any>;
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer;
}

/**
 * The preimage the browser is willing to sign, built entirely from local
 * config. Signing a server-chosen preimage would hand /api/auth/nonce the
 * spending key: the seed is SHA-256 of this signature, so whoever picks the
 * message picks the key.
 */
function buildDerivationTypedData(
  wallet: string,
  descriptor: EvmDerivationDescriptor,
): BaseDerivationTypedData {
  const pool =
    EVM_CONFIG.DERIVATION_POOL || EVM_CONFIG.CONTRACTS.SHIELDED_POOL_NOVA;
  const location = {
    wallet: wallet.toLowerCase(),
    origin: typeof window === "undefined" ? "" : window.location.origin,
    pool,
    verifier:
      EVM_CONFIG.DERIVATION_VERIFIER || EVM_CONFIG.CONTRACTS.NOVA_VERIFIER,
  };
  return {
    version: descriptor.version,
    domain: {
      name: descriptor.domainName,
      version: descriptor.keyVersion,
      chainId: EVM_CONFIG.CHAIN_ID,
      verifyingContract: pool,
    },
    types: derivationTypes(descriptor),
    message: descriptor.statement
      ? {
          action: descriptor.statement,
          ...location,
          keyVersion: descriptor.keyVersion,
        }
      : {
          ...location,
          purpose: descriptor.purpose,
          keyVersion: descriptor.keyVersion,
        },
  };
}

export function buildBaseDerivationTypedData(
  wallet: string,
): BaseDerivationTypedData {
  return buildDerivationTypedData(wallet, DERIVATION);
}

/** What this network signed before the current descriptor. */
export function buildLegacyBaseDerivationTypedData(
  wallet: string,
): BaseDerivationTypedData {
  return buildDerivationTypedData(wallet, LEGACY_DERIVATION);
}

export function assertBaseDerivationTypedData(
  value: unknown,
  wallet: string,
): BaseDerivationTypedData {
  const expected = buildBaseDerivationTypedData(wallet);
  const typedData = value as BaseDerivationTypedData;
  if (!typedData || typeof typedData !== "object") {
    throw new Error("Base derivation typed data is missing or invalid");
  }
  // Compared field-for-field rather than shape-checked: a served payload that
  // differs anywhere derives a different key, so the only safe answer is the
  // locally built one.
  if (JSON.stringify(typedData) !== JSON.stringify(expected)) {
    throw new Error(
      "Base derivation typed data does not match the expected local key derivation parameters",
    );
  }
  return expected;
}

export async function signBaseDerivationTypedData(
  signer: ethers.Signer,
  typedDataValue: unknown,
  wallet: string,
): Promise<{ signatureHex: string; signatureB64: string }> {
  return signDerivationTypedData(
    signer,
    assertBaseDerivationTypedData(typedDataValue, wallet),
  );
}

/**
 * Signs the served (checked) derivation preimage and returns the signature
 * that seeds this wallet's keys: the previous message's signature for a
 * wallet that had a private wallet before the message changed, otherwise the
 * new one (walletCarryOver.ts). `newSignatureHex` is always the signature of
 * the current preimage, for checks that compare it across sessions. Nothing
 * is saved until the caller runs `commit` for a verified connection.
 */
export async function signBaseDerivation(
  signer: ethers.Signer,
  typedDataValue: unknown,
  wallet: string,
  {
    existingWallet = false,
    useRecoveryKey = false,
    isCurrent,
    onPreviousSignature,
  }: {
    /** The user had a private wallet before the signing message changed. */
    existingWallet?: boolean;
    /** An explicit phrase or saved encrypted key supplies the private identity. */
    useRecoveryKey?: boolean;
    isCurrent?: () => boolean;
    /** Called before the one-time signature of the previous message. */
    onPreviousSignature?: () => void;
  } = {},
): Promise<{
  signatureHex: string;
  signatureB64: string;
  newSignatureHex: string;
  typedData: BaseDerivationTypedData;
  commit: () => Promise<void>;
}> {
  const typedData = assertBaseDerivationTypedData(typedDataValue, wallet);
  const { signatureHex: newSignatureHex } = await signDerivationTypedData(
    signer,
    typedData,
    useRecoveryKey,
  );
  const newSignature = ethers.getBytes(newSignatureHex);
  if (useRecoveryKey) {
    // Authenticate the public wallet, but do not try to reproduce a lost old
    // signature. The recovery vault independently checks the private identity.
    return {
      signatureHex: newSignatureHex,
      signatureB64: Buffer.from(newSignature).toString("base64"),
      newSignatureHex,
      typedData,
      commit: async () => {},
    };
  }
  const carryOver = {
    scope: `evm:${EVM_CONFIG.CHAIN_ID}:${String(
      typedData.domain.verifyingContract,
    ).toLowerCase()}`,
    wallet: wallet.toLowerCase(),
  };
  // Saved notes, history and journals name the chain ID and the wallet.
  const chainMarkers = [`_${EVM_CONFIG.CHAIN_ID}_`, `:${EVM_CONFIG.CHAIN_ID}:`];
  const decision = await resolveCarryOver(carryOver, newSignature, {
    existingWallet,
    hasPreviousWalletData: () =>
      hasSavedWalletData((key) => {
        const lower = key.toLowerCase();
        return (
          lower.includes(carryOver.wallet) &&
          chainMarkers.some((marker) => lower.includes(marker))
        );
      }),
  });
  let signatureHex = newSignatureHex;
  if (decision.kind === "carried") {
    signatureHex = ethers.hexlify(decision.signature);
  } else if (decision.kind === "needs-previous") {
    onPreviousSignature?.();
    ({ signatureHex } = await signDerivationTypedData(
      signer,
      buildLegacyBaseDerivationTypedData(wallet),
    ));
  }
  const signature = ethers.getBytes(signatureHex);
  return {
    signatureHex,
    signatureB64: Buffer.from(signature).toString("base64"),
    newSignatureHex,
    typedData,
    commit: carryOverCommit(carryOver, decision, newSignature, signature, {
      isCurrent,
    }),
  };
}

async function signDerivationTypedData(
  signer: ethers.Signer,
  typedData: BaseDerivationTypedData,
  useRecoveryKey = false,
): Promise<{ signatureHex: string; signatureB64: string }> {
  try {
    const signatureHex = await signer.signTypedData(
      typedData.domain,
      typedData.types,
      typedData.message,
    );
    if (EVM_CONFIG.CHAIN_ID === 6281971) {
      // Verify the one setup signature and compare it with previous successful
      // connections. Signing the same payload again causes a duplicate wallet
      // approval; it is not a separate sign-in challenge. Preserve the exact
      // signature bytes and derivation preimage so existing keys do not change.
      const verified = verifyDogeDerivationSignature(typedData, signatureHex);
      if (!useRecoveryKey) assertDogeDerivationFingerprint(typedData, verified);
    } else {
      // A recovered key must only be wrapped with a signature verified against
      // the locally pinned message and the connected public account.
      const recovered = ethers.verifyTypedData(
        typedData.domain,
        typedData.types,
        typedData.message,
        signatureHex,
      );
      if (
        recovered.toLowerCase() !==
        String(typedData.message.wallet).toLowerCase()
      )
        throw new Error(
          "Wallet signature did not match the connected account.",
        );
    }
    return {
      signatureHex,
      signatureB64: Buffer.from(ethers.getBytes(signatureHex)).toString(
        "base64",
      ),
    };
  } catch (error) {
    // Wallet/ethers errors may embed the signature, which is a spending secret.
    if (error instanceof DogeDerivationError) throw error;
    if (isUserRejection(error))
      throw Object.assign(new Error("Privacy key signing was cancelled."), {
        code: 4001,
      });
    throw new DogeDerivationError(
      "The wallet could not complete the privacy signature check. Reconnect and try again.",
    );
  }
}

export async function deriveBaseSpendingKeysFromSignature(
  signatureHex: string,
) {
  const signatureBytes = ethers.getBytes(signatureHex);
  const hashBuffer = await crypto.subtle.digest(
    "SHA-256",
    toArrayBuffer(new Uint8Array(signatureBytes)),
  );
  const seed = new Uint8Array(hashBuffer);
  return deriveSpendingKeys(seed);
}
